import React from 'react';
import { useIonAlert } from '@ionic/react';
import { useUpdateFlow } from '../contexts/UpdateFlowContext';

/**
 * Single confirmation dialog before the unified update overlay runs.
 */
export const UpdateOfferPrompt: React.FC = () => {
  const { activeOffer, dismissUpdateOffer, acceptUpdateAndStart } = useUpdateFlow();
  const [presentAlert] = useIonAlert();
  const lastPresentedKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!activeOffer) {
      lastPresentedKeyRef.current = null;
      return;
    }

    const key = `${activeOffer.reason}:${activeOffer.fromVersion ?? ''}:${activeOffer.toVersion}`;
    if (lastPresentedKeyRef.current === key) {
      return;
    }
    lastPresentedKeyRef.current = key;

    const fromLabel =
      activeOffer.fromVersion && activeOffer.fromVersion !== 'unknown'
        ? `v${activeOffer.fromVersion}`
        : null;

    const isSameVersion = activeOffer.fromVersion != null && activeOffer.fromVersion === activeOffer.toVersion;
    const versionLine =
      activeOffer.reason === 'version'
        ? `Da ${fromLabel ?? 'versione precedente'} a v${activeOffer.toVersion}.`
        : isSameVersion
          ? `Versione attuale: v${activeOffer.toVersion}.`
          : `Da ${fromLabel ?? 'versione precedente'} a v${activeOffer.toVersion}.`;

    const bullets =
      '• I dati memorizzati in locale dall’app verranno cancellati per allinearsi alla nuova versione.\n' +
      '• L’app si chiuderà e si riaprirà da sola.\n' +
      '• Dopo il riavvio i dati verranno scaricati di nuovo dal server (serve connessione).';

    const intro =
      activeOffer.reason === 'version'
        ? 'È stata pubblicata una nuova versione dell’applicazione.'
        : 'È disponibile un aggiornamento dei file dell’applicazione (cache / funzionalità).';

    const message = `${intro}\n\n${versionLine}\n\n${bullets}`;

    void presentAlert({
      header: 'Aggiornamento disponibile',
      subHeader: 'Conferma per procedere',
      message,
      backdropDismiss: false,
      buttons: [
        {
          text: 'Più tardi',
          role: 'cancel',
          handler: () => {
            dismissUpdateOffer();
            return true;
          },
        },
        {
          text: 'Aggiorna ora',
          handler: () => {
            void acceptUpdateAndStart();
            return true;
          },
        },
      ],
    });
  }, [activeOffer, acceptUpdateAndStart, dismissUpdateOffer, presentAlert]);

  return null;
};
